import { darken } from 'polished'
import { formatInput } from '../fields/CurrencyField'
import { CurrencyCode } from '../types/API'
import { IColorTheme, IGiftItemResponse } from '../types/itemTypes'
import { formatCurrency, getCurrencySymbol } from './currencyHelper'
import { stripEmptyFromArray } from './dataTransformer'
import colors from '../lib/colors'
import { FontSize } from '../types/organisation'

export const getItemValue = (
  input: string | undefined,
  currency: CurrencyCode,
): string => {
  if (!input) return ''
  const moneyArray = stripEmptyFromArray(input.split(','))
  if (moneyArray && moneyArray.length === 1) {
    // use the same `formatInput` from the value field so that it displays consistently
    return moneyArray[0] !== '0'
      ? `${getCurrencySymbol(currency)}${formatInput(moneyArray[0])}`
      : 'Free'
  }

  moneyArray.sort((a: string, b: string) => Number(a) - Number(b))
  return `${formatCurrency(
    parseFloat(moneyArray[0]),
    currency,
    true,
  )} - ${formatCurrency(
    parseFloat(moneyArray[moneyArray.length - 1]),
    currency,
    true,
  )}`
}

export const getItemColorTheme = (
  colorTheme?: Partial<IColorTheme>,
): IColorTheme => {
  const {
    colourBodyText,
    colourHeaderText,
    colourHighlight,
    colourPageBackground,
    colourPriceText,
    colourTextOnBackground,
    colourTextOnHighlight,
    colourHeaderBackground,
    transparentIframe,
  } = colorTheme || {}

  const withHash = `#${colourHighlight}`
  const highlight = CSS.supports('color', withHash) ? withHash : colors.logo

  return {
    colourBodyText: colourBodyText ? `#${colourBodyText}` : colors.darkerGray,
    colourHeaderText: colourHeaderText ? `#${colourHeaderText}` : highlight,
    colourHighlight: highlight,
    colourPageBackground:
      colourPageBackground && !transparentIframe
        ? `#${colourPageBackground}`
        : 'transparent',
    colourPriceText: colourPriceText ? `#${colourPriceText}` : highlight,
    colourTextOnBackground: colourTextOnBackground
      ? `#${colourTextOnBackground}`
      : colors.black,
    colourTextOnHighlight: colourTextOnHighlight
      ? `#${colourTextOnHighlight}`
      : colors.white,
    colourHeaderBackground: colourHeaderBackground
      ? `#${colourHeaderBackground}`
      : colors.white,
    fade: darken(0.1, colors.darkGray),
    hover: darken(0.1, highlight),
  }
}

// These are the standard dimensions images should be when displayed in the portal
export const ITEM_IMAGE_DIMENSIONS = { x: 237, y: 192 }
export const ITEM_HEADER_IMAGE_DIMENSIONS = { x: 650, y: 163 }
export const CATEGORY_IMAGE_DIMENSIONS = { x: 300, y: 300 }
export const VOUCHER_BANNER_DIMENSIONS = { x: 650, y: 163 }
export const ORGANISATION_LOGO_DIMENSIONS = { x: 256, y: 142 }

const IMAGE_BUCKET_URL = 'https://ik.imagekit.io/onetree'

export const getResizedImage = (
  url: string,
  dimensions: Partial<{ x: number; y: number }>,
): string => {
  if (!url) return ''
  // TODO lose this, instead make backend always just send the path
  // This removes the domain name, leaving just the path name
  const imageSlug = url.replace(/^.*\/\/[^/]+/, '')

  const x = dimensions.x ? `w-${dimensions.x}` : ''
  const y = dimensions.y ? `h-${dimensions.y}` : ''
  const config = `?tr=${`${x}${x && y ? ',' : ''}${y}`}`

  return `${`${IMAGE_BUCKET_URL}${imageSlug}${config}`}`
}

export const getGiftItemImage = (url: string): string => getResizedImage(url, ITEM_IMAGE_DIMENSIONS)

export const getCategoryImage = (url: string): string => {
  const image = getResizedImage(url, CATEGORY_IMAGE_DIMENSIONS)
  return image
}

export const COMPLIMENTARY_GIFT_ITEM_IMAGE_URL = getResizedImage(
  'https://onetree-files.ams3.digitaloceanspaces.com/gift-item-image/complimentary.jpg',
  ITEM_IMAGE_DIMENSIONS,
)
export const EXAMPLE_GIFT_ITEM_IMAGE_URL = getResizedImage(
  'https://onetree-files.ams3.digitaloceanspaces.com/gift-item-image/placeholder.jpg',
  ITEM_IMAGE_DIMENSIONS,
)

export const getRemaining = (
  quantity?: number | null,
  sold?: number | null,
): number | null => {
  const remaining = quantity && quantity - (sold || 0)
  return remaining || null
}

export const getMaxQuantity = (
  remaining?: number | null,
  maxPerOrder?: number | null,
): number => {
  // if none remaining, none allowed
  if (remaining === 0) return 0

  // if remaining not provided, return maxPerOrder or default
  if (!remaining) {
    if (!maxPerOrder) return 100
    return maxPerOrder
  }

  // if there is no max, then the remaining amount is the limit, max 100
  if (!maxPerOrder) return remaining < 100 ? remaining : 100

  // if max allowed is higher than amount remaining, amount remaining is the limit
  if (maxPerOrder > remaining) return remaining

  // otherwise, max allowed is the limit
  return maxPerOrder
}

export const isTESO = (item: IGiftItemResponse): boolean => {
  const {
    startInfo,
    questions,
    enabledQuestionIds,
    startDate,
    restrictions,
    originalPrice,
  } = item

  return Boolean(
    startInfo
    || questions
    || enabledQuestionIds
    || startDate
    || startDate === 0
    || restrictions
    || originalPrice
    || originalPrice === 0,
  )
}
export const isGiftVoucher = (item: IGiftItemResponse): boolean => {
  const {
    type, validity, description, details, maxIndividuals,
  } = item

  return Boolean(
    type
    || validity
    || validity === 0
    || description
    || details
    || maxIndividuals
    || maxIndividuals === 0,
  )
}

export const isTicket = (item: IGiftItemResponse): boolean => {
  const {
    startInfo, prices, questions, enabledQuestionIds,
  } = item

  return Boolean(
    startInfo
    || prices
    || (questions && questions.length > 0)
    || (enabledQuestionIds && enabledQuestionIds.length > 0),
  )
}

export const getFontSize = (size: FontSize): string => {
  switch (size) {
    case FontSize.Large:
      return '1.5rem'
    case FontSize.Medium:
      return '1.3rem'
    case FontSize.Small:
    default:
      return '1.1rem'
  }
}
